import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, User, Building, Phone, Eye, EyeOff, ArrowRight } from 'lucide-react';
import Button from '../components/ui/Button';
import GradientText from '../components/ui/GradientText';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import EmailVerification from '../components/EmailVerification';
import TwoFactorVerification from '../components/TwoFactorVerification';
import TwoFactorSetup from '../components/TwoFactorSetup';
import GoogleOAuthButton from '../components/GoogleOAuthButton';
import AzureOAuthButton from '../components/AzureOAuthButton';
import GoogleOAuthAdditionalInfo from '../components/GoogleOAuthAdditionalInfo';

interface AuthProps {
  onNavigate: (page: string) => void;
}

const Auth: React.FC<AuthProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { login, googleLogin, azureLogin, register, resendVerificationCode, verify2FA, updateUserProfile, user, isAuthenticated } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showVerification, setShowVerification] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [twoFAAccountId, setTwoFAAccountId] = useState('');
  const [showUnverifiedPrompt, setShowUnverifiedPrompt] = useState(false);
  const [isNewAccount, setIsNewAccount] = useState(false);
  const [showGoogleAdditionalInfo, setShowGoogleAdditionalInfo] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [loginForm, setLoginForm] = useState({
    email: '',
    password: '',
  });

  const [registerForm, setRegisterForm] = useState({
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    enterprise: '',
    rgpdConsent: false,
    newsletterConsent: false,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      setShowGoogleAdditionalInfo(false);
      setShowVerification(false);
      setShow2FA(false);
      setShow2FASetup(false);
      setShowUnverifiedPrompt(false);
      setError('');
      setSuccess('');
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setShowGoogleAdditionalInfo(false);
    setShowVerification(false);
    setShow2FA(false);
    setShow2FASetup(false);
    setShowUnverifiedPrompt(false);
    setError('');
    setSuccess('');
  }, []);

  const handleLoginSubmit = async () => {
    setLoading(true);
    setError('');
    setShowUnverifiedPrompt(false);
    try {
      const result = await login(loginForm.email, loginForm.password);
      if (result.requiresTwoFA && result.accountId) {
        setVerificationEmail(loginForm.email);
        setTwoFAAccountId(result.accountId);
        setShow2FA(true);
      } else {
        onNavigate('account');
      }
    } catch (err: any) {
      const errorMessage = err.message || t('auth.login.errors.loginFailed');
      if (errorMessage.includes('Account not activated') || errorMessage.includes('verify your email')) {
        setVerificationEmail(loginForm.email);
        setShowUnverifiedPrompt(true);
        setError('');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    if (!registerForm.rgpdConsent) {
      setError(t('auth.register.consent.required'));
      setLoading(false);
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      setError(t('auth.register.errors.passwordMismatch'));
      setLoading(false);
      return;
    }

    try {
      const { confirmPassword, ...registerData } = registerForm;
      await register(registerData);
      setVerificationEmail(registerForm.email);
      setIsNewAccount(true);
      setShowVerification(true);
    } catch (err: any) {
      setError(err.message || t('auth.register.errors.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSuccess = async () => {
    if (isNewAccount) {
      try {
        const result = await login(verificationEmail, registerForm.password);
        if (result.requiresTwoFA && result.accountId) {
          throw new Error('Unexpected 2FA requirement during new account setup');
        }
        setShowVerification(false);
        setShow2FASetup(true);
      } catch (err: any) {
        setError(t('auth.register.errors.loginAfterVerificationFailed'));
        setShowVerification(false);
        setIsLogin(true);
      }
    } else {
      try {
        if (showUnverifiedPrompt) {
          await login(verificationEmail, loginForm.password);
        } else {
          await login(verificationEmail, registerForm.password);
        }
        onNavigate('account');
      } catch (err: any) {
        setError(t('auth.register.errors.loginAfterVerificationFailed'));
        setShowVerification(false);
        setShowUnverifiedPrompt(false);
        setIsLogin(true);
      }
    }
  };

  const handleBackToRegistration = () => {
    setShowVerification(false);
    setVerificationEmail('');
    setError('');
  };

  const handleResendEmailForLogin = async () => {
    setLoading(true);
    setError('');
    
    try {
      await resendVerificationCode(verificationEmail);
      setShowUnverifiedPrompt(false);
      setShowVerification(true);
    } catch (err: any) {
      setError(err.message || t('auth.verification.errors.resendFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelVerification = () => {
    setShowUnverifiedPrompt(false);
    setVerificationEmail('');
    setError('');
  };

  const handle2FASuccess = async () => {
    try {
      onNavigate('account');
    } catch (err: any) {
      setError(t('auth.2fa.errors.loginAfter2FAFailed'));
      setShow2FA(false);
      setIsLogin(true);
    }
  };

  const handleBackTo2FA = () => {
    setShow2FA(false);
    setTwoFAAccountId('');
    setError('');
  };

  const handleEnable2FA = async () => {
    try {
      await updateUserProfile({ mail2FA: true });
      setShow2FASetup(false);
      setIsNewAccount(false);
      onNavigate('account');
    } catch (err: any) {
      throw new Error(err.message || 'Failed to enable 2FA');
    }
  };

  const handleSkip2FA = async () => {
    try {
      setShow2FASetup(false);
      setIsNewAccount(false);
      onNavigate('account');
    } catch (err: any) {
      setError(t('auth.2fa.errors.loginAfter2FAFailed'));
      setShow2FASetup(false);
      setIsLogin(true);
    }
  };

  const handleLoginInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleRegisterInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setRegisterForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleGoogleSuccess = async (accessToken: string) => {
    setLoading(true);
    setError('');

    try {
      const userData = await googleLogin(accessToken);
      
      const needsPhone = !userData.phone || userData.phone === 'Not set' || userData.phone.trim() === '';
      const needsEnterprise = !userData.enterprise || userData.enterprise === 'false' || userData.enterprise.trim() === '';
      
      if (needsPhone || needsEnterprise) {
        setShowGoogleAdditionalInfo(true);
      } else {
        onNavigate('account');
      }
    } catch (err: any) {
      setError(err.message || t('auth.google.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = (error: string) => {
    setError(error);
  };

  const handleGoogleAdditionalInfoComplete = async (additionalInfo: { phone?: string; enterprise?: string }) => {
    setLoading(true);
    setError('');

    try {
      await updateUserProfile(additionalInfo);
      setShowGoogleAdditionalInfo(false);
      onNavigate('account');
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAdditionalInfoSkip = () => {
    setShowGoogleAdditionalInfo(false);
    onNavigate('account');
  };

  const handleAzureSuccess = async (accessToken: string) => {
    setLoading(true);
    setError('');

    try {
      const userData = await azureLogin(accessToken);
      
      const needsPhone = !userData.phone || userData.phone === 'Not set' || userData.phone.trim() === '';
      const needsEnterprise = !userData.enterprise || userData.enterprise === 'false' || userData.enterprise.trim() === '';
      
      if (needsPhone || needsEnterprise) {
        setShowGoogleAdditionalInfo(true);
      } else {
        onNavigate('account');
      }
    } catch (err: any) {
      setError(err.message || t('auth.azure.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAzureError = (error: string) => {
    setError(error);
  };

  if (showVerification) {
    return (
      <EmailVerification
        email={verificationEmail}
        onVerificationSuccess={handleVerificationSuccess}
        onBack={handleBackToRegistration}
        isNewAccount={isNewAccount}
      />
    );
  }

  if (showGoogleAdditionalInfo) {
    return (
      <GoogleOAuthAdditionalInfo
        onComplete={handleGoogleAdditionalInfoComplete}
        onSkip={handleGoogleAdditionalInfoSkip}
        loading={loading}
        error={error}
      />
    );
  }

  if (show2FASetup) {
    return (
      <TwoFactorSetup
        email={verificationEmail}
        onEnable2FA={handleEnable2FA}
        onSkip={handleSkip2FA}
      />
    );
  }

  if (show2FA) {
    return (
      <TwoFactorVerification
        accountId={twoFAAccountId}
        email={verificationEmail}
        onVerificationSuccess={handle2FASuccess}
        onBack={handleBackTo2FA}
      />
    );
  }

  if (showUnverifiedPrompt) {
    return (
      <div className="min-h-screen pt-16 bg-gray-50">
        <SEO
          title={t('auth.verification.unverifiedPrompt.seo.title')}
          description={t('auth.verification.unverifiedPrompt.seo.description')}
          keywords={t('auth.verification.unverifiedPrompt.seo.keywords')}
        />

        <section className="py-12">
          <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
            >
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-yellow-600" />
                </div>
                <h1 className="text-2xl font-bold mb-2">
                  <GradientText variant="primary">{t('auth.verification.unverifiedPrompt.title')}</GradientText>
                </h1>
                <p className="text-gray-600">
                  {t('auth.verification.unverifiedPrompt.subtitle')}
                </p>
                <p className="font-medium text-gray-900 mt-2">{verificationEmail}</p>
              </div>

              <div className="space-y-4">
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  rightIcon={<ArrowRight size={16} />}
                  disabled={loading}
                  onClick={handleResendEmailForLogin}
                >
                  {loading ? t('auth.verification.unverifiedPrompt.buttons.sending') : t('auth.verification.unverifiedPrompt.buttons.sendCode')}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  size="lg"
                  className="w-full"
                  disabled={loading}
                  onClick={handleCancelVerification}
                >
                  {t('auth.verification.unverifiedPrompt.buttons.backToLogin')}
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 bg-gray-50">
      <SEO
        title={isLogin ? t('auth.login.seo.title') : t('auth.register.seo.title')}
        description={isLogin ? t('auth.login.seo.description') : t('auth.register.seo.description')}
        keywords={isLogin ? t('auth.login.seo.keywords') : t('auth.register.seo.keywords')}
      />

      <section className="py-12">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 animate-slide-up">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              {isLogin ? (
                <>
                  {t('auth.login.title.prefix')}
                  <GradientText variant="primary">{t('auth.login.title.highlight')}</GradientText>
                </>
              ) : (
                <>
                  {t('auth.register.title.prefix')}
                  <GradientText variant="primary">{t('auth.register.title.highlight')}</GradientText>
                </>
              )}
            </h1>
            <p className="text-gray-600">
              {isLogin ? t('auth.login.subtitle') : t('auth.register.subtitle')}
            </p>
          </div>

          <motion.div
            key={isLogin ? 'login' : 'register'}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
          >
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-700 text-sm">{success}</p>
              </div>
            )}

            {isLogin ? (
              <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleLoginSubmit(); }}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auth.login.form.fields.email')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="email"
                      name="email"
                      value={loginForm.email}
                      onChange={handleLoginInputChange}
                      required
                      autoComplete="email"
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      placeholder={t('auth.login.form.placeholders.email')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auth.login.form.fields.password')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type={showLoginPassword ? "text" : "password"}
                      name="password"
                      value={loginForm.password}
                      onChange={handleLoginInputChange}
                      required
                      autoComplete="current-password"
                      className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 [&::-ms-reveal]:hidden [&::-webkit-contacts-auto-fill-button]:hidden"
                      placeholder={t('auth.login.form.placeholders.password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                      aria-label={t('auth.login.form.buttons.togglePassword')}
                    >
                      {showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  rightIcon={<ArrowRight size={16} />}
                  disabled={loading}>
                  {loading ? t('auth.login.form.buttons.signInLoading') : t('auth.login.form.buttons.signIn')}
                </Button>

                <div className="text-center">
                  <p className="text-gray-600 text-sm">
                    {t('auth.login.form.links.noAccount')}
                    <button
                      onClick={() => {
                        setIsLogin(!isLogin);
                        setError('');
                        setSuccess('');
                        setShowLoginPassword(false);
                        setShowRegisterPassword(false);
                        setShowConfirmPassword(false);
                        setShowVerification(false);
                        setShow2FA(false);
                        setShow2FASetup(false);
                        setIsNewAccount(false);
                        setVerificationEmail('');
                        setTwoFAAccountId('');
                      }}
                      className="text-primary hover:underline font-medium"
                    >
                      {t('auth.login.form.links.signUp')}
                    </button>
                  </p>
                </div>

                {/* <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">{t('auth.divider.or')}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <GoogleOAuthButton
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    disabled={loading}
                  />
                  <AzureOAuthButton
                    onSuccess={handleAzureSuccess}
                    onError={handleAzureError}
                    disabled={loading}
                  />
                </div> */}
              </form>
            ) : (
              <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleRegisterSubmit(); }}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auth.register.form.fields.firstName')}
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        name="firstname"
                        value={registerForm.firstname}
                        onChange={handleRegisterInputChange}
                        required
                        autoComplete="given-name"
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                        placeholder={t('auth.register.form.placeholders.firstName')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auth.register.form.fields.lastName')}
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        name="lastname"
                        autoComplete="family-name"
                        value={registerForm.lastname}
                        onChange={handleRegisterInputChange}
                        required
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                        placeholder={t('auth.register.form.placeholders.lastName')}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auth.register.form.fields.email')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="email"
                      name="email"
                      value={registerForm.email}
                      onChange={handleRegisterInputChange}
                      required
                      autoComplete="email"
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      placeholder={t('auth.register.form.placeholders.email')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auth.register.form.fields.password')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type={showRegisterPassword ? "text" : "password"}
                      name="password"
                      value={registerForm.password}
                      onChange={handleRegisterInputChange}
                      required
                      autoComplete="new-password"
                      className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 [&::-ms-reveal]:hidden [&::-webkit-contacts-auto-fill-button]:hidden"
                      placeholder={t('auth.register.form.placeholders.password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                      aria-label={t('auth.register.form.buttons.togglePassword')}
                    >
                      {showRegisterPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auth.register.form.fields.confirmPassword')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={registerForm.confirmPassword}
                      onChange={handleRegisterInputChange}
                      required
                      autoComplete="new-password"
                      className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 [&::-ms-reveal]:hidden [&::-webkit-contacts-auto-fill-button]:hidden"
                      placeholder={t('auth.register.form.placeholders.confirmPassword')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                      aria-label={t('auth.register.form.buttons.toggleConfirmPassword')}
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auth.register.form.fields.phone')}
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="tel"
                      name="phone"
                      autoComplete="tel"
                      value={registerForm.phone}
                      onChange={handleRegisterInputChange}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      placeholder={t('auth.register.form.placeholders.phone')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auth.register.form.fields.company')}
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      name="enterprise"
                      autoComplete="organization"
                      value={registerForm.enterprise}
                      onChange={handleRegisterInputChange}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      placeholder={t('auth.register.form.placeholders.company')}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      name="rgpdConsent"
                      checked={registerForm.rgpdConsent}
                      onChange={handleRegisterInputChange}
                      required
                      className="mt-1 w-4 h-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">
                      {t('auth.register.consent.privacy')}
                    </span>
                  </label>

                  <label className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      name="newsletterConsent"
                      checked={registerForm.newsletterConsent}
                      onChange={handleRegisterInputChange}
                      className="mt-1 w-4 h-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">
                      {t('auth.register.consent.newsletter')}
                    </span>
                  </label>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  rightIcon={<ArrowRight size={16} />}
                  disabled={loading}
                >
                  {loading ? t('auth.register.form.buttons.createAccountLoading') : t('auth.register.form.buttons.createAccount')}
                </Button>

                <div className="text-center">
                  <p className="text-gray-600 text-sm">
                    {t('auth.register.form.links.hasAccount')}
                    <button
                      onClick={() => {
                        setIsLogin(!isLogin);
                        setError('');
                        setSuccess('');
                        setShowLoginPassword(false);
                        setShowRegisterPassword(false);
                        setShowConfirmPassword(false);
                        setShowVerification(false);
                        setShow2FA(false);
                        setShow2FASetup(false);
                        setIsNewAccount(false);
                        setVerificationEmail('');
                        setTwoFAAccountId('');
                      }}
                      className="text-primary hover:underline font-medium"
                    >
                      {t('auth.register.form.links.signIn')}
                    </button>
                  </p>
                </div>

                {/* <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">{t('auth.divider.or')}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <GoogleOAuthButton
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    disabled={loading}
                  />
                  <AzureOAuthButton
                    onSuccess={handleAzureSuccess}
                    onError={handleAzureError}
                    disabled={loading}
                  /> 
                </div> */}
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Auth;